import React from 'react';
import { Button } from '../ui/button';
import {
  Building2, Factory, Calendar, Briefcase, Users, ShieldCheck, Award,
  FileCheck, MessageSquare,
} from 'lucide-react';
import { Highlight, Fact } from './sharedAtoms';

/** "About Us" tab: company narrative + highlights + categories + verification docs + Quick Facts sidebar. */
const SupplierAboutTab = ({ supplier, products, onEnquire }) => (
  <div className="grid lg:grid-cols-[1fr_320px] gap-6">
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#2c54b2]" /> About {supplier.name}
        </h2>
        {supplier.description ? (
          <p className="text-sm text-gray-700 leading-relaxed">{supplier.description}</p>
        ) : (
          <p className="text-sm text-gray-500 italic">
            {supplier.name} is a verified vendor on TelanganaMart, based in {supplier.city}.
            Established in {supplier.established}, we are committed to delivering quality
            products to buyers across Telangana.
          </p>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Company Highlights</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Highlight icon={Factory} label="Nature of Business" value="Manufacturer & Exporter" />
          <Highlight icon={Calendar} label="Year of Establishment" value={String(supplier.established)} />
          <Highlight icon={Briefcase} label="Business Type" value={supplier.business_type || 'Manufacturer'} />
          <Highlight icon={Users} label="Number of Employees" value={supplier.employees || 'Small / Medium'} />
          <Highlight icon={ShieldCheck} label="GST Status" value={supplier.gst} />
          <Highlight icon={Award} label="Annual Turnover" value="₹5 - 25 Crore" />
        </div>
      </div>

      {supplier.categories && supplier.categories.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3">We Deal in</h3>
          <div className="flex flex-wrap gap-2">
            {supplier.categories.map((c) => (
              <span key={c} className="px-3 py-1.5 bg-blue-50 text-[#2c54b2] rounded-md text-sm font-medium">{c}</span>
            ))}
          </div>
        </div>
      )}

      {(supplier.company_doc_url || supplier.gst_doc_url) && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-green-600" /> Verification Documents
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {supplier.company_doc_url && (
              <a href={supplier.company_doc_url} target="_blank" rel="noreferrer"
                 className="flex items-center gap-3 p-3 border border-gray-200 rounded-md hover:border-[#ee5e23] hover:bg-orange-50 transition-colors">
                <FileCheck className="w-5 h-5 text-green-600" />
                <div className="text-sm">
                  <div className="font-medium text-gray-900">Company Registration</div>
                  <div className="text-xs text-gray-500">Click to view</div>
                </div>
              </a>
            )}
            {supplier.gst_doc_url && (
              <a href={supplier.gst_doc_url} target="_blank" rel="noreferrer"
                 className="flex items-center gap-3 p-3 border border-gray-200 rounded-md hover:border-[#ee5e23] hover:bg-orange-50 transition-colors">
                <ShieldCheck className="w-5 h-5 text-green-600" />
                <div className="text-sm">
                  <div className="font-medium text-gray-900">GST Certificate</div>
                  <div className="text-xs text-gray-500">Click to view</div>
                </div>
              </a>
            )}
          </div>
        </div>
      )}
    </div>

    <aside className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Quick Facts</h3>
        <dl className="space-y-3 text-sm">
          <Fact label="Vendor ID" value={supplier.vendor_code || supplier.id} />
          <Fact label="GSTIN" value={supplier.gst_number || '—'} />
          <Fact label="City" value={supplier.city} />
          <Fact label="Member Since" value={String(supplier.established)} />
          <Fact label="Total Products" value={String(products.length)} />
        </dl>
      </div>
      <Button onClick={onEnquire} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11">
        <MessageSquare className="w-4 h-4 mr-2" /> Get Best Quote
      </Button>
    </aside>
  </div>
);

export default SupplierAboutTab;
